import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { IconButton } from '../ui/IconButton';
import { FeatherArrowLeft, FeatherArrowRight } from '../ui/icons';
import { Filter, Search, ChevronDown, ChevronUp, Info, Paperclip } from 'react-feather';
import { workPlanApi, taskApi } from '../../services/api';
import './WorkPlan.css';
import { ProjectBadge } from '../ui/project-badge';
import TaskTypeIcon from '../shared/TaskTypeIcon';
import TaskDueDate from '../shared/TaskDueDate';
import { FeatureTag } from '../ui/feature-tag';
import TaskSummaryModal from '../../pages/components/TaskSummaryModal';
import api from '../../services/api';
import { isManager } from '../../utils/roleUtils';
import TaskTypeSelect, { TASK_TYPES } from '../shared/TaskTypeSelect';

const TASK_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CLOSED: 'closed',
  RELEASED: 'released'
};

const NON_SELECTABLE_STATUSES = [TASK_STATUS.COMPLETED, TASK_STATUS.CLOSED, TASK_STATUS.RELEASED];

const TaskHistory = ({ task }) => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const taskData = await taskApi.get(task.id);
        setHistory(taskData.history);
      } catch (error) {
        console.error('Failed to fetch task history:', error);
      }
    };
    fetchHistory();
  }, [task.id]);

  if (!history) return null;

  const totalHours = history.reduce((sum, record) => sum + (record.hours || 0), 0);
  const firstRecord = history[history.length - 1];

  return (
    <div className="task-history">
      <div className="history-item">
        <span className="history-label">First Added:</span>
        <span className="history-value">
          {new Date(firstRecord?.created_at).toLocaleDateString()}
        </span>
      </div>
      <div className="history-item">
        <span className="history-label">Total Hours:</span>
        <span className="history-value">{totalHours}h</span>
      </div>
    </div>
  );
};

const TaskCard = ({ task, showStatus = true, isCollapsible = false }) => {
  const [isExpanded, setIsExpanded] = useState(!isCollapsible);

  return (
    <div className="task-card">
      <div className="task-card-header">
        <div className="task-card-title">
          <TaskTypeIcon taskType={task.task_type} />
          <h5>{task.title}</h5>
          {isCollapsible && (
            <button 
              className="expand-button"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? '−' : '+'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {task.feature_tag && (
            <FeatureTag 
              name={task.feature_tag.name} 
              color={task.feature_tag.color} 
              size="small" 
            />
          )}
          <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} />
          {showStatus && (
            <div className={`task-status ${task.status.toLowerCase()}`}>
              {task.status}
            </div>
          )}
        </div>
      </div>
      <div className={`task-card-content ${isCollapsible && !isExpanded ? 'collapsed' : ''}`}>
        {task.description && (
          <div className="task-description">
            {task.description}
          </div>
        )}
        {task.hours > 0 && (
          <div className="task-hours">
            Hours Logged: {task.hours}
          </div>
        )}
        {task.notes && (
          <div className="task-notes">
            <p>{task.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const DraggableTask = ({ task, index, isWorkDayStarted }) => {
  const isSelectable = !NON_SELECTABLE_STATUSES.includes(task.status);
  
  return (
    <Draggable 
      draggableId={task.id.toString()} 
      index={index} 
      isDragDisabled={isWorkDayStarted || !isSelectable}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`task-item ${isWorkDayStarted ? 'active' : ''} ${!isSelectable ? 'disabled' : ''} ${snapshot.isDragging ? 'dragging' : ''}`}
          title={!isSelectable ? `Cannot add ${task.status} tasks to work plan` : ''}
        >
          <TaskCard 
            task={task} 
            isCollapsible={true}
            showStatus={true}
          />
        </div>
      )}
    </Draggable>
  );
};

const TaskList = ({ droppableId, tasks, isWorkDayStarted }) => (
  <Droppable droppableId={droppableId}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className={`task-list ${droppableId}-tasks ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
      >
        {tasks.map((task, index) => (
          <DraggableTask
            key={task.id.toString()}
            task={task}
            index={index}
            isWorkDayStarted={isWorkDayStarted}
          />
        ))}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);

const TaskItem = ({ task, onMove, onTaskClick }) => {
  const isSelectable = !NON_SELECTABLE_STATUSES.includes(task.status);

  const handleTaskClick = (e) => {
    // Don't trigger if clicking on the move button or its parent
    if (e.target.closest('.icon-button') || e.target.closest('button')) {
      return;
    }
    if (onTaskClick) {
      onTaskClick(task);
    }
  };

  return (
    <div 
      className={`task-item ${!isSelectable ? 'disabled' : ''} ${onTaskClick ? 'clickable' : ''}`}
      title={!isSelectable ? `Cannot add ${task.status} tasks to work plan` : onTaskClick ? 'Click to view task details' : ''}
      onClick={onTaskClick ? handleTaskClick : undefined}
      style={onTaskClick ? { cursor: 'pointer' } : {}}
    >
      <div className="task-item-header">
        <span className="task-item-title">
          <TaskTypeIcon taskType={task.task_type} />
          {task.display_id && (
            <span className="text-sm font-medium text-neutral-600 mr-2">
              [{task.display_id}]
            </span>
          )}
          {task.title}
        </span>
        <div className="flex items-center gap-2">
          <ProjectBadge projectName={task.project?.name} />
          {task.feature_tag && (
            <FeatureTag 
              name={task.feature_tag.name} 
              color={task.feature_tag.color} 
              size="small" 
            />
          )}
          <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} />
          <Badge variant={task.status.toLowerCase() === TASK_STATUS.IN_PROGRESS.toLowerCase() ? "warning" : undefined}>
            {task.status}
          </Badge>
          {onTaskClick && (
            <button
              className="task-view-details-btn"
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick(task);
              }}
              title="View task details"
            >
              <Info size={16} />
            </button>
          )}
          {isSelectable && (
            <IconButton 
              icon={onMove.direction === 'right' ? <FeatherArrowRight /> : <FeatherArrowLeft />}
              onClick={() => onMove.handler(task)}
            />
          )}
        </div>
      </div>
      {task.description && (
        <p className="task-item-description">{task.description}</p>
      )}
      {((task.attachments && task.attachments.length > 0) || (task.work_plan_attachments && task.work_plan_attachments.length > 0)) && (
        <div className="task-item-attachments">
          <Paperclip size={14} className="task-item-attachments-icon" />
          <span className="task-item-attachments-label">
            {(() => {
              const taskAttachments = task.attachments?.length || 0;
              const workPlanAttachments = task.work_plan_attachments?.length || 0;
              const total = taskAttachments + workPlanAttachments;
              if (taskAttachments > 0 && workPlanAttachments > 0) {
                return `${total} attachment${total !== 1 ? 's' : ''} (${taskAttachments} task, ${workPlanAttachments} work plan)`;
              } else if (taskAttachments > 0) {
                return `${taskAttachments} attachment${taskAttachments !== 1 ? 's' : ''}`;
              } else {
                return `${workPlanAttachments} work plan attachment${workPlanAttachments !== 1 ? 's' : ''}`;
              }
            })()}
          </span>
        </div>
      )}
      {/* <div className="task-item-footer">
        {task.dateStarted && (
          <span>Started: {new Date(task.dateStarted).toLocaleDateString()}</span>
        )}
        {task.hours !== undefined && (
          <span>Hours: {task.hours}</span>
        )}
      </div> */}
    </div>
  );
};

const WorkPlan = () => {
  const { user } = useAuth();
  const [workPlan, setWorkPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allTasks, setAllTasks] = useState([]); // Store all tasks
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [newTask, setNewTask] = useState({ title: '', description: '' });
  const [unclosedWorkPlans, setUnclosedWorkPlans] = useState([]);
  const [isWorkDayStarted, setIsWorkDayStarted] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [filters, setFilters] = useState({
    searchQuery: '',
    status: [],
    project: '',
    taskType: '',
    featureTag: ''
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
  const [userIsManager, setUserIsManager] = useState(false);
  const navigate = useNavigate();
  const statusDropdownRef = React.useRef(null);

  useEffect(() => {
    loadWorkPlanAndTasks();
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const response = await api.get('/api/auth/role');
      const userRole = response.data?.role;
      setUserIsManager(isManager(userRole));
    } catch (err) {
      console.error('Error checking user role:', err);
    }
  };

  const loadWorkPlanAndTasks = async () => {
    try {
      const [activePlan, unclosedPlans, tasks] = await Promise.all([
        workPlanApi.getActive(),
        workPlanApi.getUnclosed(),
        taskApi.getAll()
      ]);

      // If there's an active work plan or unclosed plans, redirect to dashboard
      if (activePlan || (unclosedPlans && unclosedPlans.length > 0)) {
        navigate('/dashboard');
        return;
      }

      // Filter out completed tasks
      const filteredTasks = tasks.filter(task => task.status !== TASK_STATUS.COMPLETED);

      setWorkPlan(activePlan);
      setUnclosedWorkPlans(unclosedPlans);
      setAllTasks(filteredTasks);
      setAvailableTasks(filteredTasks);
      setIsWorkDayStarted(!!activePlan);
    } catch (error) {
      console.error('Failed to load work plan:', error);
      setError('Failed to load work plan and tasks');
    } finally {
      setLoading(false);
    }
  };

  const createNewWorkPlan = async () => {
    try {
      const newPlan = await workPlanApi.create({ tasks: [] });
      setWorkPlan(newPlan);
      setError(null);
      return newPlan;
    } catch (error) {
      console.error('Failed to create work plan:', error);
      if (error.response?.data?.error) {
        // Show the error message from the server
        setError(error.response.data.error);
        // Add a delay before redirecting to ensure user sees the message
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError('Failed to create new work plan');
      }
      throw error;
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    // Get the task being dragged
    const sourceItems = source.droppableId === 'available' 
      ? [...availableTasks] 
      : [...selectedTasks];
    const draggedTask = sourceItems[source.index];

    // Prevent dragging if task is non-selectable
    if (NON_SELECTABLE_STATUSES.includes(draggedTask.status)) {
      return;
    }
    
    if (source.droppableId === destination.droppableId) {
      // Reordering within the same list
      const items = source.droppableId === 'available' 
        ? [...availableTasks] 
        : [...selectedTasks];
      
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      if (source.droppableId === 'available') {
        setAvailableTasks(items);
      } else {
        setSelectedTasks(items);
      }
    } else {
      // Moving between lists - update selectedTasks, filteredAvailableTasks will auto-update
      if (source.droppableId === 'available') {
        // Moving from available to selected
        const newAvailable = [...availableTasks];
        const newSelected = [...selectedTasks];
        const [movedItem] = newAvailable.splice(source.index, 1);
        newSelected.splice(destination.index, 0, movedItem);
        setSelectedTasks(newSelected);
        // availableTasks will be updated by useEffect when filteredAvailableTasks changes
      } else {
        // Moving from selected to available
        const newSelected = [...selectedTasks];
        const newAvailable = [...availableTasks];
        const [movedItem] = newSelected.splice(source.index, 1);
        newAvailable.splice(destination.index, 0, movedItem);
        setSelectedTasks(newSelected);
        setAvailableTasks(newAvailable);
      }
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    
    try {
      // Create the task and optionally assign it to the work plan
      const taskData = {
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        work_plan_id: workPlan?.id,
        user_id: user.id
      };

      const newTaskObj = await taskApi.create(taskData);
      
      // Add to allTasks if not completed
      if (newTaskObj.status !== TASK_STATUS.COMPLETED) {
        setAllTasks(prev => [...prev, newTaskObj]);
      }
      
      if (workPlan) {
        setSelectedTasks(prev => [...prev, newTaskObj]);
      } else {
        setAvailableTasks(prev => [...prev, newTaskObj]);
      }

      setNewTask({ title: '', description: '' });
    } catch (error) {
      console.error('Failed to create task:', error);
      setError('Failed to create task');
    }
  };

  const handleStartWorkDay = async () => {
    if (selectedTasks.length === 0) return;
    
    try {
      let currentPlan = workPlan;

      // Create a new work plan if one doesn't exist
      if (!currentPlan) {
        currentPlan = await createNewWorkPlan();
      }

      // Add all selected tasks to the work plan using the new API endpoint
      await workPlanApi.addTasks(currentPlan.id, selectedTasks);

      // Update all selected tasks to IN_PROGRESS status
      await Promise.all(selectedTasks.map(task => 
        taskApi.update(task.id, {
          status: TASK_STATUS.IN_PROGRESS
        })
      ));

      setIsWorkDayStarted(true);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to start work day:', error);
      setError(error.message || 'Failed to start work day. Please try again.');
    }
  };

  const handleSubmitPlan = useCallback(() => {
    console.log('Work plan submitted');
    navigate('/dashboard');
  }, [navigate]);

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const projects = new Set();
    const taskTypes = new Set();
    const featureTags = new Set();
    
    allTasks.forEach(task => {
      if (task.project?.id) {
        projects.add(JSON.stringify({ id: task.project.id, name: task.project.name }));
      }
      if (task.task_type) {
        taskTypes.add(task.task_type);
      }
      if (task.feature_tag?.id) {
        featureTags.add(JSON.stringify({ id: task.feature_tag.id, name: task.feature_tag.name }));
      }
    });

    return {
      projects: Array.from(projects).map(p => JSON.parse(p)),
      taskTypes: Array.from(taskTypes).sort((a, b) => {
        const idxA = TASK_TYPES.indexOf(a);
        const idxB = TASK_TYPES.indexOf(b);
        return (idxA === -1 ? TASK_TYPES.length : idxA) - (idxB === -1 ? TASK_TYPES.length : idxB);
      }),
      featureTags: Array.from(featureTags).map(t => JSON.parse(t))
    };
  }, [allTasks]);

  // Filter available tasks based on filter criteria
  const filteredAvailableTasks = useMemo(() => {
    let filtered = allTasks.filter(task => {
      // Exclude tasks that are already selected
      return !selectedTasks.find(st => st.id === task.id);
    });

    // Apply search query
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => {
        const title = (task.title || '').toLowerCase();
        const description = (task.description || '').toLowerCase();
        const displayId = (task.display_id || '').toLowerCase();
        return title.includes(query) || description.includes(query) || displayId.includes(query);
      });
    }

    // Apply status filter
    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter(task => filters.status.includes(task.status));
    }

    // Apply project filter
    if (filters.project) {
      filtered = filtered.filter(task => task.project?.id === filters.project);
    }

    // Apply task type filter
    if (filters.taskType) {
      filtered = filtered.filter(task => (task.task_type || 'Task') === filters.taskType);
    }

    // Apply feature tag filter
    if (filters.featureTag) {
      if (filters.featureTag === 'untagged') {
        filtered = filtered.filter(task => !task.feature_tag?.id);
      } else {
        filtered = filtered.filter(task => task.feature_tag?.id === filters.featureTag);
      }
    }

    return filtered;
  }, [allTasks, selectedTasks, filters]);

  // Update available tasks when filters change
  useEffect(() => {
    setAvailableTasks(filteredAvailableTasks);
  }, [filteredAvailableTasks]);

  const handleStatusToggle = (status) => {
    setFilters(prev => {
      const currentStatuses = prev.status || [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter(s => s !== status)
        : [...currentStatuses, status];
      return { ...prev, status: newStatuses };
    });
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: '',
      status: [],
      project: '',
      taskType: '',
      featureTag: ''
    });
    setStatusDropdownOpen(false);
  };

  const statusOptions = [
    { value: TASK_STATUS.OPEN, label: 'Open' },
    { value: TASK_STATUS.IN_PROGRESS, label: 'In Progress' }
  ];

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownOpen]);

  // Helper function to calculate task summary data
  const calculateTaskSummary = useCallback((task) => {
    if (!task.work_plan_tasks || task.work_plan_tasks.length === 0) {
      return {
        totalHours: 0,
        occurrences: 0,
        work_plan_attachments: []
      };
    }

    const totalHours = task.work_plan_tasks.reduce((sum, wpt) => sum + (parseFloat(wpt.hours) || 0), 0);
    const occurrences = task.work_plan_tasks.length;
    
    // Collect all attachments from work plan tasks
    const attachments = [];
    task.work_plan_tasks.forEach(wpt => {
      if (wpt.work_plan_attachments && wpt.work_plan_attachments.length > 0) {
        attachments.push(...wpt.work_plan_attachments);
      }
    });

    return {
      totalHours: totalHours.toFixed(1),
      occurrences,
      work_plan_attachments: attachments
    };
  }, []);

  // Function to open task summary modal
  const openTaskSummaryModal = useCallback(async (task) => {
    try {
      // Fetch full task details with work_plan_tasks
      const fullTask = await taskApi.get(task.id);
      const summary = calculateTaskSummary(fullTask);
      setSelectedTaskForModal({
        ...fullTask,
        ...summary
      });
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      // Fallback to using the task we have
      const summary = calculateTaskSummary(task);
      setSelectedTaskForModal({
        ...task,
        ...summary
      });
    }
  }, [calculateTaskSummary]);

  // Function to close task summary modal
  const closeTaskSummaryModal = useCallback(() => {
    setSelectedTaskForModal(null);
  }, []);

  // Helper function to get task status
  const getTaskStatus = useCallback((taskId) => {
    const task = allTasks.find(t => t.id === taskId) || selectedTaskForModal;
    return task?.status || 'open';
  }, [allTasks, selectedTaskForModal]);

  // Helper function to get status variant for badge
  const getStatusVariant = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'in-progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'released':
        return 'success';
      case 'open':
        return 'neutral';
      default:
        return 'neutral';
    }
  }, []);

  // Helper function to get task history from work_plan_tasks
  const getTaskHistory = useCallback((taskId) => {
    const task = selectedTaskForModal || allTasks.find(t => t.id === taskId);
    if (!task || !task.work_plan_tasks || task.work_plan_tasks.length === 0) {
      return [];
    }

    return task.work_plan_tasks
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .map(wpt => {
        const dateObj = new Date(wpt.created_at);
        return {
          date: dateObj,
          hours: wpt.hours || 0,
          notes: wpt.notes || '',
          status: wpt.status === 'completed' ? 'Completed' : 'In Progress',
          attachments: wpt.work_plan_attachments || []
        };
      });
  }, [allTasks, selectedTaskForModal]);

  if (loading) return <div>Loading...</div>;
  if (error) return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Button 
            variant="neutral-tertiary" 
            icon={<FeatherArrowLeft />}
            onClick={() => navigate('/dashboard')}
          >
            Back
          </Button>
          <span className="text-2xl font-semibold text-default-font">Create Work Plan</span>
        </div>
      </div>
      <div className="alert alert-error">
        <h3>Error</h3>
        <p>{error}</p>
        <p>Redirecting to dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Button 
            variant="neutral-tertiary" 
            icon={<FeatherArrowLeft />}
            onClick={() => navigate('/dashboard')}
          >
            Back
          </Button>
          <span className="text-2xl font-semibold text-default-font">Create Work Plan</span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="user-avatar">
            <span className="user-initials">
              {user?.displayName?.split(' ').map(n => n[0]).join('') || 'U'}
            </span>
          </div>
          <span className="text-sm text-default-font">{user?.displayName?.split(' ')[0]}</span>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-md border border-neutral-border bg-default-background px-6 py-6 shadow-sm">
        <TextField label="Task title">
          <TextField.Input 
            placeholder="Enter task title..."
            value={newTask.title}
            onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
          />
        </TextField>
        <TextArea label="Task description">
          <TextArea.Input 
            placeholder="Enter task description..."
            value={newTask.description}
            onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
          />
        </TextArea>
        <Button onClick={handleAddTask} className="add-task">Add Task</Button>
      </div>

      {/* Advanced Filters */}
      <div className="task-controls-section">
        <div className="task-controls-header" onClick={() => setFiltersCollapsed(!filtersCollapsed)} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
            <h3 className="task-controls-title">
              <Filter size={16} />
              Advanced Filters
            </h3>
          </div>
          <button 
            className="stats-toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              setFiltersCollapsed(!filtersCollapsed);
            }}
            aria-label={filtersCollapsed ? "Expand filters" : "Collapse filters"}
          >
            {filtersCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
        <div className={`task-controls task-controls-compact ${filtersCollapsed ? 'collapsed' : ''}`}>
          <div className="filter-controls-compact-wrapper">
            {/* Search Box */}
            <div className="task-search-section-compact">
              <div className="task-search-wrapper-compact">
                <Search size={14} className="task-search-icon-compact" />
                <input
                  type="text"
                  className="task-search-input-compact"
                  placeholder="Search..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                />
                {filters.searchQuery && (
                  <button
                    className="task-search-clear-compact"
                    onClick={() => setFilters(prev => ({ ...prev, searchQuery: '' }))}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="filter-controls-compact">
            {/* Status Filter */}
            <div className="filter-group-compact">
              <label htmlFor="filter-status" className="filter-label-compact">
                <span className="filter-label-text-compact">Status</span>
              </label>
              <div className="filter-select-wrapper-compact status-multiselect-wrapper-compact" ref={statusDropdownRef}>
                <div 
                  className="status-multiselect-trigger-compact"
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setStatusDropdownOpen(false);
                    }
                  }}
                >
                  <span className="status-multiselect-display">
                    {filters.status && filters.status.length > 0
                      ? `${filters.status.length} selected`
                      : 'All Statuses'}
                  </span>
                  <ChevronDown size={14} className={`status-arrow ${statusDropdownOpen ? 'open' : ''}`} />
                </div>
                {statusDropdownOpen && (
                  <div className="status-multiselect-dropdown-compact">
                    {statusOptions.map(option => {
                      const isSelected = filters.status && filters.status.includes(option.value);
                      return (
                        <label 
                          key={option.value}
                          className="status-multiselect-option-compact"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleStatusToggle(option.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Project Filter */}
            {filterOptions.projects.length > 0 && (
              <div className="filter-group-compact">
                <label htmlFor="filter-project" className="filter-label-compact">
                  <span className="filter-label-text-compact">Project</span>
                </label>
                <div className="filter-select-wrapper-compact">
                  <select
                    id="filter-project"
                    value={filters.project}
                    onChange={(e) => setFilters(prev => ({ ...prev, project: e.target.value }))}
                    className="filter-select-compact"
                  >
                    <option value="">All Projects</option>
                    {filterOptions.projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Task Type Filter */}
            {filterOptions.taskTypes.length > 0 && (
              <div className="filter-group-compact">
                <label htmlFor="filter-task-type" className="filter-label-compact">
                  <span className="filter-label-text-compact">Type</span>
                </label>
                <div className="filter-select-wrapper-compact">
                  <TaskTypeSelect
                    id="filter-task-type"
                    value={filters.taskType}
                    onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
                    className="filter-select-compact"
                    includeAllOption
                    options={filterOptions.taskTypes}
                  />
                </div>
              </div>
            )}

            {/* Feature Tag Filter */}
            {filterOptions.featureTags.length > 0 && (
              <div className="filter-group-compact">
                <label htmlFor="filter-feature-tag" className="filter-label-compact">
                  <span className="filter-label-text-compact">Tag</span>
                </label>
                <div className="filter-select-wrapper-compact">
                  <select
                    id="filter-feature-tag"
                    value={filters.featureTag}
                    onChange={(e) => setFilters(prev => ({ ...prev, featureTag: e.target.value }))}
                    className="filter-select-compact"
                  >
                    <option value="">All Tags</option>
                    <option value="untagged">Untagged</option>
                    {filterOptions.featureTags.map(tag => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Clear Filters Button */}
            {(filters.searchQuery || filters.status.length > 0 || filters.project || filters.taskType || filters.featureTag) && (
              <button
                className="clear-filters-btn-compact"
                onClick={clearFilters}
                title="Clear all filters"
              >
                <span>Clear</span>
              </button>
            )}
            </div>
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex w-full items-start gap-6">
          {/* Available Tasks */}
          <div className="task-column">
            <span className="text-sm font-semibold text-default-font">AVAILABLE TASKS</span>
            <Droppable droppableId="available">
              {(provided, snapshot) => (
                <div 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`droppable-area ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                >
                  {availableTasks.map((task, index) => (
                    <Draggable 
                      key={task.id} 
                      draggableId={task.id.toString()} 
                      index={index}
                      isDragDisabled={isWorkDayStarted}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={snapshot.isDragging ? 'dragging' : ''}
                        >
                          <TaskItem 
                            task={task}
                            onMove={{
                              direction: 'right',
                              handler: (task) => {
                                setSelectedTasks(prev => [...prev, task]);
                                // availableTasks will be updated by useEffect when filteredAvailableTasks changes
                              }
                            }}
                            onTaskClick={openTaskSummaryModal}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* Selected Tasks */}
          <div className="task-column">
            <span className="text-sm font-semibold text-default-font">SELECTED TASKS</span>
            <Droppable droppableId="selected">
              {(provided, snapshot) => (
                <div 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`droppable-area ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                >
                  {selectedTasks.map((task, index) => (
                    <Draggable 
                      key={task.id} 
                      draggableId={task.id.toString()} 
                      index={index}
                      isDragDisabled={isWorkDayStarted}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={snapshot.isDragging ? 'dragging' : ''}
                        >
                          <TaskItem 
                            task={task}
                            onMove={{
                              direction: 'left',
                              handler: (task) => {
                                setSelectedTasks(prev => prev.filter(t => t.id !== task.id));
                                // availableTasks will be updated by useEffect when filteredAvailableTasks changes
                              }
                            }}
                            onTaskClick={openTaskSummaryModal}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>

      <Button 
        size="large" 
        className="w-full"
        onClick={handleStartWorkDay}
        disabled={selectedTasks.length === 0}
      >
        Start Work for the Day
      </Button>

      {/* Task Summary Modal */}
      <TaskSummaryModal
        task={selectedTaskForModal}
        isOpen={!!selectedTaskForModal}
        onClose={closeTaskSummaryModal}
        getTaskStatus={getTaskStatus}
        getStatusVariant={getStatusVariant}
        getTaskHistory={getTaskHistory}
        availableTags={[]}
        allTasks={allTasks}
        projectId={selectedTaskForModal?.project?.id}
        allowCrossProject={false}
        isManager={userIsManager}
        onTaskClick={openTaskSummaryModal}
      />
    </div>
  );
};

export default WorkPlan; 